import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DashboardShell } from '@/components/layout/DashboardShell';
import { adminService } from '@/services/admin';
import { AttendanceStatsCards } from '@/components/admin/attendance/AttendanceStatsCards';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

export default function AdminAttendanceSchoolPage() {
    const { data, isLoading } = useQuery({
        queryKey: ['school-attendance-summary'],
        queryFn: () => adminService.getSchoolAttendanceSummary({ period: 'daily' }),
    });

    const [viewMode, setViewMode] = useState<'summary' | 'absentees' | 'late'>('summary');

    return (
        <DashboardShell title="School Attendance" role="admin">
            <div className="space-y-8">
                {/* 1. Stats Cards */}
                <AttendanceStatsCards
                    summary={data?.summary || { total: 0, present: 0, absent: 0, late: 0, leave: 0, messageSent: 0 }}
                    loading={isLoading}
                />

                {/* 2. Detailed Lists (Simplified for MVP) */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Recent Absentees */}
                    <Card className="p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="font-bold text-slate-800">Recent Absentees</h3>
                            <Button variant="secondary">View All</Button>
                        </div>
                        <div className="space-y-3">
                            {data?.records?.filter((r: any) => r.status === 'ABSENT').slice(0, 5).map((record: any) => (
                                <div key={record.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                    <div>
                                        <div className="font-medium text-slate-900">{record.studentName}</div>
                                        <div className="text-xs text-slate-500">{record.className}</div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded-full">SMS not set up</span>
                                    </div>
                                </div>
                            ))}
                            {(!data?.records || data.records.length === 0) && <p className="text-slate-400 text-sm">No absentees today.</p>}
                        </div>
                    </Card>

                    {/* Class-wise Breakdown */}
                    <Card className="p-6">
                        <h3 className="font-bold text-slate-800 mb-4">Class Performance</h3>
                        <div className="space-y-3 max-h-[400px] overflow-y-auto">
                            {data?.classes?.sort((a: any, b: any) => b.absent - a.absent).map((cls: any) => (
                                <div key={cls.id} className="flex items-center justify-between p-3 border border-slate-100 rounded-lg hover:bg-slate-50 transition-colors">
                                    <div className="font-medium text-slate-900">{cls.name}</div>
                                    <div className="flex gap-4 text-sm">
                                        <span className="text-emerald-600 font-medium">{cls.present} Present</span>
                                        <span className="text-rose-600 font-medium">{cls.absent} Absent</span>
                                    </div>
                                </div>
                            ))}
                            {(!data?.classes || data.classes.length === 0) && <p className="text-slate-400 text-sm">No class data available.</p>}
                        </div>
                    </Card>
                </div>
            </div>
        </DashboardShell>
    );
}
